import BigNumber from "bignumber.js"

/**
 * 矿池类
 */
export class Pool {

    /**
     * 构造函数
     * @param mining 关联的挖矿对象
     * @param properties 矿池属性，如: { cpb: 1, gpb: 0 }，其中: cpb = coin_per_block; gpb = gem_per_block
     * @param options 选项信息，暂时忽略
     */
    constructor(mining, properties, options) {
        this.mining = mining
        this.initialize(properties)
    }

    /**
     * 初始化
     * @param properties 矿池属性，如: { cpb: 1, gpb: 0 }，其中: cpb = coin_per_block; gpb = gem_per_block
     */
    initialize(properties) {
        this.properties = properties ?? {}
    }

}

/**
 * 仓储仓类
 */
export class Storage {

    /**
     * 构造函数
     * @param mining 关联的挖矿对象
     * @param properties 仓储属性，如: { capacity: 1000, stock: { coins: 0, gems: 0 } }
     * @param options 选项信息，可选项：{ claimCallback: null }
     */
    constructor(mining, properties, options) {
        this.mining = mining

        // 初始化
        this.initialize(properties)

        // 领取库存量到服务端回调方法
        this.claimCallback = options?.claimCallback
    }

    /**
     * 计算存储仓存量百分比
     * @param stock 存储仓存量信息
     * @returns {number|number} 存量百分比，如：80% 值为 80
     * @private
     */
    _calculatePercent(stock) {
        const value = stock?.coins ?? 0
        const capacity = this.properties?.capacity ?? 0
        const percent = capacity > 0
            ? Number((new BigNumber(value).div(capacity).times(100).toNumber()).toFixed(2))
            : 0
        return percent
    }

    /**
     * 初始化
     * @param properties 仓储属性，如: { capacity: 1000, stock: { coins: 0, gems: 0 } }
     */
    initialize(properties) {
        this.properties = properties ?? {}
        this.initialStock = Object.assign({ coins: 0, gems: 0, percent: 0 }, properties?.stock ?? {})
        this.stock = { ...this.initialStock }
        if (this.stock.percent === null || this.stock.percent === undefined) {
            this.stock.percent = this._calculatePercent(this.stock)
        }
    }

    /**
     * 入库新挖的存量到存储仓
     * @param coins 新挖的金币总量
     * @param gems 新挖的宝石总量
     * @returns { blocks}
     */
    store(coins, gems) {
        const capacity = this.properties.capacity ?? 0

        // 如果仓储已满，那么直接返回
        if (this.coins >= capacity) {
            return this.stock
        }

        // 在初始存量基础上累加新挖的存量
        const total = {
            coins: new BigNumber(this.initialStock?.coins ?? 0).plus(coins ?? 0).toNumber(),
            gems: new BigNumber(this.initialStock?.gems ?? 0).plus(gems ?? 0).toNumber(),
        }

        // 如果存储容量未超限，那么直接存入
        if (capacity <= 0 || total.coins <= capacity) {
            this.stock.coins = total.coins
            this.stock.gems = total.gems
            this.stock.percent = this._calculatePercent(this.stock)
            return this.stock
        }

        // 计算可新增入库的存量
        const remainingCapacity = new BigNumber(capacity).minus(this.initialStock?.coins ?? 0).toNumber()
        const incCoins = Math.min(remainingCapacity, coins)
        const incGems = Math.min(new BigNumber(incCoins).div(coins).times(gems).toNumber(), gems)

        // 计算最终存量
        this.stock.coins = new BigNumber(this.initialStock?.coins ?? 0).plus(incCoins).toNumber()
        this.stock.gems = new BigNumber(this.initialStock?.gems ?? 0).plus(incGems).toNumber()
        this.stock.percent = this._calculatePercent(this.stock)

        // 返回结果
        return this.stock
    }

    /**
     * 领取存储仓中的资产到服务端的资产余额中
     * @returns {{code, data, message}|*}
     */
    async claim() {
        try {
            // 领取到服务端前判断
            if (typeof(this.claimCallback) != 'function') {
                return Mining.result(501, 'Claim method undefined')
            }

            // 领取到服务端前的操作
            this._claimBefore()

            // 调用领取 API 接口并返回
            const response = await this.claimCallback(this.stock)

            // 领取到服务端后的操作
            this._claimAfter(response)

            // 返回领取到服务端结果
            return response
        } catch (error) {
            console.log(`[Storage] ++++++++++ claim error: `, error)
            return Mining.result(500, error.message || 'System error')
        }
    }

    /**
     * 领取到服务端余额前的操作
     * @private
     */
    _claimBefore() {
        //
    }

    /**
     * 领取到服务端余额后的操作
     * @param response 领取 API 接口返回的结果，示例: { code: 0, message: '', data: { results: {}, balance: {}, stock: {} } }
     * @private
     */
    _claimAfter(response) {
        // 领取失败时， 不执行任何操作
        if (response.code !== 0) {
            return
        }

        // 更新挖矿信息
        const miningInfo = response.data?.mining
        if (this.mining && miningInfo) {
            this.mining.reset(miningInfo)
        }
    }

}

/**
 * 总矿机类，融合了所有矿机的挖矿能力
 */
export class TotalMiner {

    /**
     * 构造函数
     * @param mining 关联的挖矿对象
     * @param properties 总矿机属性，如: { summary: { eph: 1000 } }，其中: eph = earnings_per_hour
     * @param options 选项信息，暂时忽略
     */
    constructor(mining, properties, options) {
        this.mining = mining
        this.initialize(properties)
    }

    /**
     * 初始化
     * @param properties 总矿机属性，如: { summary: { eph: 1000 } }，其中: eph = earnings_per_hour
     */
    initialize(properties) {
        if (properties && 'summary' in properties) {
            properties = properties.summary
        }
        this.properties = properties ?? { eph: 0 }
        if (!this.properties.eph && this.properties.pph) {
            this.properties.eph = this.properties.pph
            delete this.properties.pph
        }
    }

}

/**
 * 挖矿类，所有矿机都融合在一起进行挖矿
 */
export class Mining {

    /**
     * 构造函数
     * @param miningInfo 挖矿信息，如: {
     *      pool: { cpb: 1, gpb: 0 },
     *      miner: { summary: { pph: 0, current_time: 0, last_calc_time: 0 }},
     *      storage: { capacity: 0, coins: 0, gems: 0 }
     * }
     * @param options 选项信息，可选项：{ claimCallback: null }
     */
    constructor(miningInfo, options) {
        // 参数验证
        if (!miningInfo) {
            throw new Error('Invalid mining info')
        }

        // 设置状态值
        this._debug = options?.debug ?? false
        this._initialized = false

        // 构造矿池、矿机、存储对象
        this.pool = new Pool(this, miningInfo?.pool ?? {}, options)
        this.storage = new Storage(this, miningInfo?.storage ?? {}, options)
        this.miner = new TotalMiner(this, miningInfo?.miner ?? { summary: {} }, options)

        // 初始化挖矿信息
        this._initialize(miningInfo)
        this._initialized = true
    }

    /**
     * 初始化
     * @param miningInfo 挖矿信息
     * @param forUpdate 是否是更新操作
     */
    _initialize(miningInfo, forUpdate = false) {
        // 重新初始化各挖矿对象
        if (!forUpdate || miningInfo?.pool) {
            this.pool.initialize(miningInfo?.pool ?? {})
        }
        if (!forUpdate || miningInfo?.storage) {
            this.storage.initialize(miningInfo?.storage ?? {})
        }
        if (!forUpdate || miningInfo?.miner) {
            this.miner.initialize(miningInfo?.miner ?? {})

            const currentTimeMs = this.miner?.properties?.current_time ?? Date.now()
            this.timeOffset = Number(BigInt(Date.now().toString()) - BigInt(currentTimeMs.toString()))
        }

        // 计算结果信息
        return this.calculate()
    }

    /**
     * 获取当前时间字符串，格式示例: 2025-01-01 00:00:00
     * @returns {string}
     * @private
     */
    _getCurrentTimeStr() {
        const now = new Date()
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')
        const hh = String(now.getHours()).padStart(2, '0')
        const min = String(now.getMinutes()).padStart(2, '0')
        const ss = String(now.getSeconds()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`
    }

    /**
     * 重新初始化
     * @param miningInfo 新的挖矿信息，如: { pool: {}, miner: {}, storage: {} }
     */
    reset(miningInfo) {
        if (miningInfo) {
            return this._initialize(miningInfo, true)
        } else {
            return this.calculate()
        }
    }

    /**
     * 计算到当前时刻为止的挖矿总收益，并更新存储仓信息。计算收益的开始时间是从服务端下发的最后一次计算收益时间算起
     * @returns {*|{result: {blocks: number, gems: number, coins: number}, storage: {blocks: number, gems: number, coins: number}}}
     */
    calculate() {
        // 获取矿池和矿机数值配置
        const poolProperties = this.pool ? this.pool.properties : {}
        const minerProperties = this.miner ? this.miner.properties : {}

        const blocksPerSecond = (minerProperties?.eph ?? 0) / 3600
        const coinsPerBlock = poolProperties?.cpb ?? 0
        const gemsPerBlock = poolProperties?.gpb ?? 0

        const lastCalcTime = minerProperties?.last_calc_time ?? 0

        // 计算挖矿时长（秒数）
        const durationMs = Number(BigInt(Date.now().toString()) - BigInt(this.timeOffset.toString()) - BigInt(lastCalcTime.toString()))
        const durationSecs = Math.floor(durationMs / 1000) // 持续秒数（取整）

        // 计算挖得的总矿块数、总金币数、总宝石数
        const blocksBN = new BigNumber(blocksPerSecond).times(durationSecs)
        const blocks = blocksBN.toNumber()
        const coins = Math.floor(blocksBN.times(coinsPerBlock).toNumber())
        const gems = blocksBN.times(gemsPerBlock).toNumber()

        // 存入到存储仓，入库时如果发现超过容量限制，那么存储仓的入库方法中会自动进行调整
        if (this.storage) {
            this.storage.store(coins, gems)
            if (this._debug && this._initialized) {
                const info = {
                    cpb: poolProperties?.cpb,
                    gpb: poolProperties?.gpb,
                    eph: minerProperties?.eph,
                    last_calc_time: minerProperties?.last_calc_time,
                    duration_ms: durationMs,
                    duration_secs: durationSecs,
                    computed: { blocks, coins, gems },
                    stock: this.storage.stock,
                }
                const time = this._getCurrentTimeStr()
                console.log(`[${time}] [Mining] ++++++++++ calculate -> store to storage: `, info)
            }
        }

        // 存储计算结果
        if (!this.result) {
            this.result = {}
        }
        this.result.blocks = blocks
        this.result.coins = coins
        this.result.gems = gems

        // 返回结果
        return {
            result: this.result, // 挖矿结果
            stock: this.storage ? this.storage.stock : Mining.getEmptyEarnings(), // 当前存储仓数值
        }
    }

    /**
     * 领取存储仓中的资产到服务端的资产余额中
     * @returns {{code, data, message}|*}
     */
    async claim() {
        return await this.storage.claim()
    }

    // ++++++++++ 以下定义一些通用的操作 ++++++++++

    /**
     * 返回标准的输出结果
     * @param code 结果代码
     * @param message 结果信息
     * @param data 结果数据
     * @returns {{code, data, message}}
     * @private
     */
    static result(code, message, data) {
        return { code, message, data }
    }

    static getEmptyEarnings() {
        return {
            coins: 0,
            gems: 0,
        }
    }

    static isEmptyEarnings(value) {
        const coins = value?.coins ?? 0
        const gems = value?.gems ?? 0
        return coins === 0 && gems === 0
    }

    static isEarningsExceeded(value, limit) {
        const valueGolds = value?.coins ?? 0
        const valueChests = value?.gems ?? 0

        const limitGolds = limit?.coins ?? 0
        const limitChests = limit?.gems ?? 0

        return valueGolds > limitGolds || valueChests > limitChests
    }

    static addEarnings(value1, value2) {
        return {
            coins: new BigNumber(value1?.coins ?? 0).plus(value2?.coins ?? 0).toNumber(),
            gems: new BigNumber(value1?.gems ?? 0).plus(value2?.gems ?? 0).toNumber(),
        }
    }

    static subEarnings(value1, value2, allowNegative = false) {
        const result = {
            coins: new BigNumber(value1?.coins ?? 0).minus(value2?.coins ?? 0).toNumber(),
            gems: new BigNumber(value1?.gems ?? 0).minus(value2?.gems ?? 0).toNumber(),
        }
        if (!allowNegative) {
            result.coins = Math.max(result.coins, 0)
            result.gems = Math.max(result.gems, 0)
        }
        return result
    }

}

export default { Pool, Storage, Mining }
